import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Download, History } from "lucide-react";
import { toast } from "sonner";
import { Card, SectionTitle, TornaHome } from "@/components/Kpi";
import { api, apiError } from "@/lib/api";
import { MESI, dateIt } from "@/lib/format";

const GIORNI_LBL = { lunedi: "lunedì", martedi: "martedì", mercoledi: "mercoledì", giovedi: "giovedì", venerdi: "venerdì" };
const lunediDi = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x.toISOString().slice(0, 10); };
const sposta = (iso, giorni) => { const x = new Date(iso); x.setDate(x.getDate() + giorni); return x.toISOString().slice(0, 10); };
const gg = (iso, i) => new Date(sposta(iso, i)).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });

function Griglia({ dati, onCambia }) {
  const { colonne, giorni, fasce, celle, opzioni, finestra, colonne_modificabili: mod, lunedi } = dati;
  const chiave = (g, f, c) => `${g}_${f}_${c}`;
  return (
    <div className="overflow-x-auto">
      <table className="text-sm border-collapse min-w-[1100px] w-full" data-testid="calendario-griglia">
        <thead>
          <tr>
            <th rowSpan={2} className="bg-yellow-200 border border-slate-300 px-2 py-1 text-xs font-bold whitespace-nowrap">
              {gg(lunedi, 0)} – {gg(lunedi, 4)}
            </th>
            {giorni.map((g, i) => (
              <th key={g} colSpan={colonne.length} className="bg-[#9EB6E4] border border-slate-300 px-2 py-1 font-semibold">
                {GIORNI_LBL[g]} <span className="font-normal text-xs">{gg(lunedi, i)}</span>
              </th>
            ))}
          </tr>
          <tr>
            {giorni.map((g) => colonne.map((c) => (
              <th key={`${g}-${c.key}`} style={{ background: c.colore }}
                className="border border-slate-300 px-1 py-1 text-[11px] font-bold tracking-wide">{c.label}</th>
            )))}
          </tr>
        </thead>
        <tbody>
          {fasce.map((f) => (
            <tr key={f}>
              <td className="bg-[#9EB6E4] border border-slate-300 px-2 py-6 text-xs font-bold">{f}</td>
              {giorni.map((g) => colonne.map((c) => {
                const k = chiave(g, f, c.key);
                const cella = celle[k] || {};
                const puo = finestra.aperta && mod.includes(c.key);
                return (
                  <td key={k} style={{ background: c.colore }} className="border border-slate-300 p-0.5"
                    title={cella.modificato_da ? `${cella.valore || "—"} · ${cella.modificato_da} · ${dateIt(cella.modificato_il)}` : ""}>
                    <select value={cella.valore || ""} disabled={!puo} data-testid={`cella-${k}`}
                      onChange={(e) => onCambia(g, f, c.key, e.target.value)}
                      className={`w-full bg-transparent text-[11px] font-medium px-1 py-2 rounded
                        ${puo ? "cursor-pointer hover:bg-white/60" : "appearance-none cursor-default"}
                        ${cella.valore ? "text-slate-900" : "text-slate-400"}`}>
                      <option value="">—</option>
                      {opzioni.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </td>
                );
              }))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Cronologia({ lunedi, tick, colonne }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/calendario-corto/cronologia?lunedi=${lunedi}`).then((r) => setRows(r.data.rows)).catch(() => setRows([]));
  }, [lunedi, tick]);
  const lbl = (k) => colonne.find((c) => c.key === k)?.label || k;
  return (
    <Card className="p-5" data-testid="calendario-cronologia">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
        <History size={14} /> Cronologia modifiche · settimana
      </div>
      {rows.length === 0 ? <div className="text-sm text-slate-400">Nessuna modifica registrata.</div> : (
        <ul className="divide-y divide-slate-100 text-sm max-h-72 overflow-y-auto">
          {rows.map((r, i) => (
            <li key={i} className="py-2 flex flex-wrap gap-x-3 gap-y-0.5" data-testid="cronologia-riga">
              <span className="text-slate-400 text-xs w-32 shrink-0">{dateIt(r.ts)} {new Date(r.ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
              <span className="font-medium">{GIORNI_LBL[r.giorno]} {r.fascia} · {lbl(r.colonna)}</span>
              <span className="font-mono text-xs">{r.prima || "—"} → <b>{r.dopo || "—"}</b></span>
              <span className={`text-xs ${r.per_conto_di ? "text-amber-700 font-semibold" : "text-slate-500"}`}>
                {r.per_conto_di ? `modificato da ${r.user_name} per conto di ${lbl(r.colonna)}` : `da ${r.user_name}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ModificheUtenti({ lunedi, tick, colonne }) {
  const [dati, setDati] = useState(null);
  useEffect(() => {
    api.get(`/calendario-corto/modifiche-utenti?lunedi=${lunedi}`).then((r) => setDati(r.data)).catch(() => setDati(null));
  }, [lunedi, tick]);
  if (!dati) return null;
  return (
    <Card className="p-5" data-testid="calendario-modifiche-utenti">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
          Modifiche per utente · settimana
        </div>
        <span className="text-xs text-slate-500" data-testid="modifiche-totale">{dati.totale} modifiche in totale</span>
      </div>
      {dati.rows.length === 0 ? <div className="text-sm text-slate-400">Nessuna modifica in questa settimana.</div> : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left py-2 pr-3">Utente</th>
              <th className="text-right py-2 px-3">Modifiche</th>
              <th className="text-right py-2 px-3">Proprie</th>
              <th className="text-right py-2 px-3">Per conto di altri</th>
              {colonne.map((c) => <th key={c.key} className="text-right py-2 px-3" style={{ background: c.colore }}>{c.label}</th>)}
              <th className="text-right py-2 pl-3">Ultima</th>
            </tr>
          </thead>
          <tbody>
            {dati.rows.map((r) => (
              <tr key={r.user_email} className="border-b border-slate-100" data-testid={`modifiche-utente-${r.user_email}`}>
                <td className="py-2 pr-3"><div className="font-medium">{r.user_name}</div><div className="text-xs text-slate-400">{r.user_email}</div></td>
                <td className="py-2 px-3 text-right font-mono font-bold">{r.totale}</td>
                <td className="py-2 px-3 text-right font-mono">{r.proprie}</td>
                <td className={`py-2 px-3 text-right font-mono ${r.per_conto_di ? "text-amber-700 font-semibold" : ""}`}>{r.per_conto_di}</td>
                {colonne.map((c) => <td key={c.key} className="py-2 px-3 text-right font-mono">{r.per_colonna[c.key] || <span className="text-slate-300">—</span>}</td>)}
                <td className="py-2 pl-3 text-right text-xs text-slate-500">{dateIt(r.ultima)} {new Date(r.ultima).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function Report({ colonne }) {
  const [mesi, setMesi] = useState([]);
  const [sel, setSel] = useState(null);
  const [dati, setDati] = useState(null);
  useEffect(() => {
    api.get("/calendario-corto/mesi").then((r) => { setMesi(r.data.mesi); setSel(r.data.mesi[0]); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!sel) return;
    api.get(`/calendario-corto/report?anno=${sel.anno}&mese=${sel.mese}`).then((r) => setDati(r.data)).catch(() => setDati(null));
  }, [sel]);
  return (
    <Card className="p-5" data-testid="calendario-report">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
          Presenze in filiale dall'inizio del mese
        </div>
        <div className="flex items-center gap-2">
          <select value={sel ? `${sel.anno}-${sel.mese}` : ""} data-testid="report-mese"
            onChange={(e) => { const [a, m] = e.target.value.split("-").map(Number); setSel({ anno: a, mese: m }); }}
            className="border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white">
            {mesi.map((m) => <option key={`${m.anno}-${m.mese}`} value={`${m.anno}-${m.mese}`}>{MESI[m.mese - 1]} {m.anno}</option>)}
          </select>
          {sel && (
            <a href={`/api/calendario-corto/report.xlsx?anno=${sel.anno}&mese=${sel.mese}`} data-testid="report-xlsx"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#0018A8] text-white text-sm hover:bg-[#001480] transition-colors duration-200">
              <Download size={14} /> Estrai Excel
            </a>
          )}
        </div>
      </div>
      {dati && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left py-2 pr-3">Filiale</th>
                {colonne.map((c) => <th key={c.key} className="text-right py-2 px-3" style={{ background: c.colore }}>{c.label}<div className="font-normal normal-case">mezze g. · giorni</div></th>)}
                <th className="text-right py-2 pl-3">Totale</th>
              </tr>
            </thead>
            <tbody>
              {dati.rows.map((r) => (
                <tr key={r.filiale} className="border-b border-slate-100" data-testid={`report-riga-${r.filiale}`}>
                  <td className="py-2 pr-3 font-medium">{r.filiale}</td>
                  {colonne.map((c) => (
                    <td key={c.key} className="py-2 px-3 text-right font-mono">
                      {r[c.key].mezze ? <>{r[c.key].mezze} <span className="text-slate-400">· {r[c.key].giorni}</span></> : <span className="text-slate-300">—</span>}
                    </td>
                  ))}
                  <td className="py-2 pl-3 text-right font-mono font-semibold">{r.totale_mezze || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-slate-400 mt-2">
            Mezze giornate = mattina e pomeriggio contano 1 ciascuno; giorni = giornate distinte. Saronno (sede) non conta.
          </p>
        </div>
      )}
    </Card>
  );
}

export default function CalendarioCorto() {
  const [lunedi, setLunedi] = useState(() => lunediDi(new Date()));
  const [dati, setDati] = useState(null);
  const [tick, setTick] = useState(0);
  const [come, setCome] = useState(() => localStorage.getItem("calendario_vedi_come") || "");

  const carica = useCallback(() => {
    api.get(`/calendario-corto/settimana?lunedi=${lunedi}${come ? `&come=${come}` : ""}`).then((r) => setDati(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [lunedi, come]);
  const cambiaVista = (v) => { setCome(v); localStorage.setItem("calendario_vedi_come", v); };
  useEffect(() => { carica(); }, [carica]);

  const cambia = async (giorno, fascia, colonna, valore) => {
    try {
      const { data } = await api.put("/calendario-corto/cella", { lunedi, giorno, fascia, colonna, valore });
      setDati((d) => ({ ...d, celle: { ...d.celle, [`${giorno}_${fascia}_${colonna}`]: data.cella || { valore } } }));
      setTick((t) => t + 1);
    } catch (e) { toast.error(apiError(e)); carica(); }
  };

  if (!dati) return <div className="text-sm text-slate-500" data-testid="calendario-loading">Caricamento…</div>;
  const f = dati.finestra;
  const puoScrivere = dati.colonne_modificabili.length > 0;

  return (
    <div className="space-y-6" data-testid="calendario-corto-page">
      <TornaHome testId="btn-calendario-torna-home" />
      <SectionTitle
        sub={`Presenze settimanali in filiale · ${puoScrivere ? "compili le colonne: " + dati.colonne_modificabili.join(", ").toUpperCase() : "sola lettura"}`}
        right={<div className="flex items-center gap-2">
          {dati.puo_scegliere_vista && (
            <select value={come} onChange={(e) => cambiaVista(e.target.value)} data-testid="calendario-vedi-come"
              title="Vedi il calendario come lo vede un'operatrice"
              className={`px-3 py-1.5 rounded-md border text-sm mr-2 focus:outline-none ${come
                ? "border-amber-400 bg-amber-50 text-amber-900" : "border-slate-300 bg-white text-slate-700"}`}>
              <option value="">Vista Master</option>
              {dati.colonne.map((c) => <option key={c.key} value={c.key}>Vedi come {c.label}</option>)}
            </select>
          )}
          <button type="button" onClick={() => setLunedi(sposta(lunedi, -7))} data-testid="settimana-prec"
            className="p-2 rounded-md border border-slate-300 hover:bg-slate-100 transition-colors duration-200"><ChevronLeft size={16} /></button>
          <button type="button" onClick={() => setLunedi(lunediDi(new Date()))} data-testid="settimana-oggi"
            className="px-3 py-1.5 rounded-md border border-slate-300 text-sm hover:bg-slate-100 transition-colors duration-200">Questa settimana</button>
          <button type="button" onClick={() => setLunedi(sposta(lunedi, 7))} data-testid="settimana-succ"
            className="p-2 rounded-md border border-slate-300 hover:bg-slate-100 transition-colors duration-200"><ChevronRight size={16} /></button>
        </div>}>
        Calendario corto
      </SectionTitle>

      <div data-testid="finestra-stato"
        className={`rounded-md px-4 py-2 text-sm border ${f.aperta ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-slate-100 border-slate-200 text-slate-600"}`}>
        {f.aperta
          ? `Settimana compilabile fino a venerdì ${dateIt(f.chiude)} alle 17:00.`
          : `${f.motivo}. Apertura: ${dateIt(f.apre)} 17:30 · chiusura: ${dateIt(f.chiude)} 17:00.`}
      </div>

      <Card className="p-3"><Griglia dati={dati} onCambia={cambia} /></Card>

      {dati.vede_cronologia && <ModificheUtenti lunedi={lunedi} tick={tick} colonne={dati.colonne} />}
      {dati.vede_cronologia && <Cronologia lunedi={lunedi} tick={tick} colonne={dati.colonne} />}
      {dati.vede_report && <Report colonne={dati.colonne} />}
    </div>
  );
}
